import React, { createContext, useState, useEffect, useCallback } from 'react';
import {jwtDecode} from 'jwt-decode';
import api from '../utils/api';

// Create context
export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);

  
  // Check user subscription status
  const checkSubscription = useCallback(async () => {
    try {
      const res = await api.get('/api/payments/subscription-status');
      setHasActiveSubscription(res.data.isActive || false);
      
      // Update user object with subscription info if user exists
      if (user && res.data.subscriptionId) {
        setUser(prev => ({
          ...prev,
          subscriptionId: res.data.subscriptionId,
          subscriptionStatus: res.data.status
        }));
      }
      
      return res.data;
    } catch (err) {
      console.error('Error checking subscription:', err);
      setHasActiveSubscription(false);
      return { isActive: false };
    }
  }, [user]); // Only depend on user

  // Check if user is authenticated
  useEffect(() => {
    const loadUser = async () => {
      setLoading(true);
      if (token) {
        try {
          // Validate token
          const decoded = jwtDecode(token);

          // Check if token is expired
          if (decoded.exp * 1000 < Date.now()) {
            logout();
            setLoading(false);
            return;
          }

          // Get user data
          const res = await api.get('/api/auth/me');
          setUser(res.data);
          localStorage.setItem('user', JSON.stringify(res.data)); // <-- Add this line

          setIsEmailVerified(res.data.isEmailVerified || false);
          setHasActiveSubscription(res.data.hasActiveSubscription || false);

          setLoading(false);
        } catch (err) {
          console.error('Error loading user:', err);
          logout();
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    };

    loadUser();
  }, [token]);

  // Register user
  const register = async (userData) => {
    try {
      const res = await api.post('/api/auth/register', userData);

      // Set token
      localStorage.setItem('token', res.data.token);
      setToken(res.data.token);

      // Fetch and store user profile
      const userRes = await api.get('/api/auth/me');
      setUser(userRes.data);
      localStorage.setItem('user', JSON.stringify(userRes.data));

      setIsEmailVerified(userRes.data.isEmailVerified || false);

      return userRes.data;
    } catch (err) {
      throw err.response.data;
    }
  };
  
  // Login user
  const login = async (userData) => {
    try {
      const res = await api.post('/api/auth/login', userData);

      // Set token
      localStorage.setItem('token', res.data.token);
      setToken(res.data.token);

      // Fetch and store user profile
      const userRes = await api.get('/api/auth/me');
      setUser(userRes.data);
      localStorage.setItem('user', JSON.stringify(userRes.data));

      setIsEmailVerified(userRes.data.isEmailVerified || false);

      return userRes.data;
    } catch (err) {
      throw err.response.data;
    }
  };
  
  // Verify email
  const verifyEmail = async (token) => {
    try {
      const res = await api.get(`/api/auth/verify-email/${token}`);
      
      // Update email verification status
      setIsEmailVerified(true);
      
      // Update user data if available
      if (user) {
        setUser(prev => ({ ...prev, isEmailVerified: true }));
      }
      
      return res.data;
    } catch (err) {
      throw err.response.data;
    }
  };
  
  // Resend verification email
  const resendVerificationEmail = async () => {
    try {
      const res = await api.post('/api/auth/resend-verification');
      return res.data;
    } catch (err) {
      throw err.response.data;
    }
  };
  
  // Logout user
  const logout = () => {
    setUser(null);
    setToken(null);
    setIsEmailVerified(false);
    setHasActiveSubscription(false);
    localStorage.removeItem('token');
    localStorage.removeItem('user'); // <-- Remove user from localStorage on logout
  };
  
  // Update profile
  const updateProfile = async (userData) => {
    try {
      const res = await api.put('/api/users/profile', userData);
      setUser(res.data);
      return res.data;
    } catch (err) {
      throw err.response.data;
    }
  };

  // Refresh user data
  const refreshUser = async () => {
    try {
      setLoading(true);

      const storedToken = localStorage.getItem('token');
      if (!storedToken) {
        setUser(null);
        setToken(null);
        localStorage.removeItem('user'); // <-- Remove user if no token
        return;
      }

      try {
        // Validate token
        const decoded = jwtDecode(storedToken);

        // Check if token is expired
        if (decoded.exp * 1000 < Date.now()) {
          logout();
          return;
        }

        api.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;

        // Fetch fresh user data
        const response = await api.get('/api/auth/me');
        
        if (response.data) {
          setUser(response.data);
          localStorage.setItem('user', JSON.stringify(response.data));
          
          // Set email verification status from user data
          setIsEmailVerified(response.data.isEmailVerified || false);
          
          // Update subscription status
          await checkSubscription();
        }
      } catch (error) {
        console.error('Error refreshing user data:', error);
        // If there's an error, don't logout automatically
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        isAuthenticated: !!user,
        isEmailVerified,
        hasActiveSubscription,
        register,
        login,
        logout,
        updateProfile,
        verifyEmail,
        resendVerificationEmail,
        checkSubscription,
        refreshUser
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};